import type { DocumentVersion } from "@prismicio/editor-fields"
import { useOnChange } from "@prismicio/editor-support/React"
import { useState } from "react"

import { useCollaborationDataFlag } from "../../../app/integrations/AmplitudeExperiments"
import type { DocumentVersionStatus } from "../../../app/services/core"
import { getReleaseId } from "../../../app/services/release"
import { useCollaborationState } from "../../collaboration/CollaborationStateProvider"
import { useDocument } from "../../DocumentProvider"
import { useGoToDocumentVersion } from "../../documentRouting"
import { getLocalVersion } from "./VersionPanel.utils"

export function useVersionPanel() {
  const { draft, version, edited, versions } = useDocument()

  function selectVersion(newVersion: DocumentVersion) {
    // If newVersion has status "Release", perform different validations
    if (newVersion.status === "release") {
      selectReleaseVersion(newVersion)
      return
    }

    // Don't do anything if we're already on the selected version.
    if (!edited && version?.status === newVersion.status) return

    navigateToVersion({
      status: newVersion.status,
      versionId: newVersion.version_id,
      releaseId: getReleaseId(newVersion),
    })
  }

  function selectReleaseVersion(newVersion: DocumentVersion) {
    if (newVersion.status !== "release") return
    // Don't do anything if we're already on the selected release.
    if (!edited && releaseSelection === newVersion.release_id) return

    navigateToVersion({
      status: newVersion.status,
      versionId: newVersion.version_id,
      releaseId: getReleaseId(newVersion),
    })
  }

  const animationDuration = 250
  const goToDocumentVersion = useGoToDocumentVersion()

  const [hideLocalDraft, setHideLocalDraft] = useState(false)

  const collaborationState = useCollaborationState()
  const canUseCollaborationData = useCollaborationDataFlag() === "on"

  const local =
    collaborationState.enabled && canUseCollaborationData
      ? // TODO: This is a temporary fix to avoid showing the local draft version
        // when autosave is enabled. Liveblocks storage will instantly update and
        // store data, this data is then passed to our backend for saving.
        // This means that the local draft version is not needed anymore.
        //
        // We need to maintain the local draft version for the time being
        // to avoid breaking the current implementation.
        undefined
      : getLocalVersion(version, draft, hideLocalDraft)

  const versionSelection = local ? undefined : version?.version_id
  const [localVersionSelection, setLocalVersionSelection] =
    useState(versionSelection)

  useOnChange(versionSelection, setLocalVersionSelection)

  const releaseSelection =
    !local && version?.status === "release" ? version.release_id : undefined

  const [localReleaseSelection, setLocalReleaseSelection] =
    useState(releaseSelection)

  useOnChange(releaseSelection, setLocalReleaseSelection)

  interface NavigateToVersionProps {
    status: DocumentVersionStatus
    versionId: string
    releaseId: string | undefined
  }
  function navigateToVersion(props: NavigateToVersionProps) {
    const { status, versionId, releaseId } = props

    // react-router is async but we want instant feedback.
    setLocalVersionSelection(versionId)
    setLocalReleaseSelection(releaseId)

    function navigateNow() {
      draft.animateNextChange(versionId)
      goToDocumentVersion({
        status,
        releaseId: releaseId,
      })
    }

    // If we cancel the local draft, wait before updating the main document
    // as it's confusing to have two concurrent but unsynchronized animations
    // playing after a single local click action.
    if (draft.editedAt) {
      setHideLocalDraft(true)
      setTimeout(() => {
        draft.cancel()
        setHideLocalDraft(false)
        navigateNow()
      }, 2 * animationDuration)
    } else navigateNow()
  }

  return {
    local,
    selectVersion,
    localVersionSelection,
    localReleaseSelection,
    versions,
  }
}
